import * as React from 'react';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { ModalFrameFooter, ModalFrameHeader, ModalFrameInner, ModalFrameMessage } from 'components/modal';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { Form, FormField } from 'components/form';
import Popover from 'components/popover/Popover';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';

import BankDetailsSettings from 'modules/organizations/components/settings/BankDetailsSettings';
import BankDetailsSettingsForm from 'modules/organizations/components/settings/BankDetailsSettingsForm';
import InfluencerBusinessDetailsSettings from 'modules/organizations/components/settings/influencer/InfluencerBusinessDetailsSettings';
import InfluencerBusinessDetailsSettingsForm from 'modules/organizations/components/settings/influencer/InfluencerBusinessDetailsSettingsForm';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { danger } from 'redux-store/reducers/common-reducer';
import {
  hideBusinessAndBankDetailsConfirmModal,
  showBusinessAndBankDetailsConfirmModal
} from 'modules/influencers-agency/reducers/influencers-agencies-reducer';
import { saveOrganizationSettings } from 'modules/organizations/reducers/organizationSettings';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { API } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';
import { uploadFile } from 'utils/upload-asset';
import {
  getPaymentDetailsExplanationType,
  prepareSettingsPayload,
  shouldInfluencerFillBankDetails,
  shouldInfluencerFillBusinessDetails
} from 'utils/organization';
import { addProtocol } from 'utils/url';
import { isRequired } from 'utils/validators';
import { logError } from 'utils/log';

import './business-bank-details.scss';

applyTranslations(require('./business-bank-details.i18n.json'));

import type {
  OrganizationSettingsForm,
  OrganizationSettingsParams,
  ProofDocumentFile,
  UploadFileResponse
} from 'types';

type FilesToUpload = Array<{
  file: ProofDocumentFile,
  name: string,
  type: string
}>;

type OwnProps = {
  subcampaignId: number,
  settingsId: number,
  organizationSettings: OrganizationSettingsForm,
  minHeight?: number,
  forceEditMode?: boolean,
  isTalentAgency: boolean, // eslint-disable-line react/no-unused-prop-types
  onCloseModal: () => void,
  onContinue: () => void
};

type DispatchProps = {
  dispatchShowConfirmModal: (params) => void,
  dispatchHideConfirmModal: () => void,
  dispatchSaveOrganizationSettings: (
    id: number,
    data: OrganizationSettingsForm,
    params: OrganizationSettingsParams
  ) => Promise<void>,
  dispatchNotifyThatEditingIsNotAnOption?: () => void,
  dispatchShowLoading: (url: string) => void,
  dispatchHideLoading: (url: string) => void,
  dispatchShowError: (errorMessage: string) => void
}

type Props = OwnProps & DispatchProps;

type State = {
  disclaimer: boolean,
  editMode: boolean,
  areDetailsMissing: boolean,
  isModified: boolean,
  country: string | null,
  organizationDetails: OrganizationSettingsForm,
  skipBusinessDetails: boolean,
  areBusinessDetailsMissing: boolean,
  filesToUpload: FilesToUpload | null
};

const msgKey = 'business_bank_details';

class BusinessBankDetails extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const areDetailsMissing = this.areDetailsMissing();
    const areBusinessDetailsMissing = shouldInfluencerFillBusinessDetails(this.props.organizationSettings);

    this.state = {
      disclaimer: false,
      organizationDetails: this.getOrganizationDetailsState(this.props),
      country: null,
      editMode: (this.props.forceEditMode || areDetailsMissing)
        && !props.dispatchNotifyThatEditingIsNotAnOption,
      isModified: false,
      areDetailsMissing,
      areBusinessDetailsMissing,
      skipBusinessDetails: false,
      filesToUpload: null
    };
  }

  componentDidUpdate(prevProps: Props): void {
    if (!isEqual(prevProps, this.props)) {
      this.setState({
        organizationDetails: this.getOrganizationDetailsState(this.props)
      });
    }
  }

  areDetailsMissing = () => shouldInfluencerFillBankDetails(this.props.organizationSettings)
  || shouldInfluencerFillBusinessDetails(this.props.organizationSettings);

  getOrganizationDetailsState(nextProps: Props): OrganizationSettingsForm {
    const { organizationSettings } = nextProps;
    const { billingAddress, businessDetails, bankDetails, proofDocuments } = organizationSettings;
    const entrepreneur = this.handleBooleanValue(businessDetails?.entrepreneur);

    return {
      bankDetails: {
        accountHolder: bankDetails?.accountHolder ?? '',
        accountNumber: bankDetails?.accountNumber ?? '',
        swift: bankDetails?.swift ?? ''
      },
      billingAddress: {
        legalName: billingAddress?.legalName ?? '',
        addressLine1: billingAddress?.addressLine1 ?? '',
        city: billingAddress?.city ?? '',
        state: billingAddress?.state ?? '',
        postalCode: billingAddress?.postalCode ?? '',
        country: billingAddress?.country ?? '',
        phone: billingAddress?.phone ?? ''
      },
      businessDetails: {
        companyName: businessDetails?.companyName ?? '',
        registeredBusiness: true,
        taxId: businessDetails?.taxId ?? '',
        website: businessDetails?.website ?? '',
        entrepreneur,
        taxIdCategory: businessDetails?.taxIdCategory || null
      },
      proofDocuments
    };
  }

  getCountryType(): string | null {
    const { organizationSettings } = this.props;
    const country = this.state.country ?? organizationSettings?.billingAddress?.country;

    return getPaymentDetailsExplanationType(country);
  }

  handleBooleanValue(value) {
    return value || (value === false ? false : '');
  }

  handleLocationInput = (event, location) => {
    const { organizationDetails } = this.state;
    const details = organizationDetails.billingAddress;

    const updatedLocation = {
      city: location.city || details.city || '',
      state: location.state || details.state || '',
      country: location.country || details.country || ''
    };

    this.setState({
      isModified: true,
      country: location.country,
      organizationDetails: {
        ...organizationDetails,
        billingAddress: {
          ...(organizationDetails.billingAddress || {}),
          ...updatedLocation
        }
      }
    });
  };

  handleTextInput = (event: React.ChangeEvent<HTMLInputElement>, type: string, shouldBeInUpperCase: boolean = false) => {
    const input = event.target;
    const value = shouldBeInUpperCase ? (input.value || '').toUpperCase() : input.value;

    this.handleChange(value, input.name, type);
  };

  handleChange = (
    value: UploadFileResponse | ProofDocumentFile | string | null,
    name: string,
    type?: string
  ) => {
    if (!type) {
      this.setState(state => ({
        organizationDetails: {
          ...state.organizationDetails,
          [name]: value
        },
        isModified: true
      }));
    } else {
      this.setState(state => ({
        organizationDetails: {
          ...state.organizationDetails,
          [type]: {
            ...state.organizationDetails[type],
            [name]: value
          }
        },
        isModified: true
      }));
    }
  };

  handlePreUpload = (
    value: ProofDocumentFile,
    name: string,
    type: string
  ): void => {
    this.handleChange(value, name, type);
    this.setState(state => ({
      filesToUpload: (state.filesToUpload || [])
        .concat({ file: value, name, type })
    }));
  };

  handleUpload = async (
    filesToUpload: FilesToUpload
  ): Promise<boolean> => {
    const { dispatchShowLoading, dispatchHideLoading, dispatchShowError } = this.props;
    const url = API.END_POINTS.API.UPLOAD_DOCUMENT;

    dispatchShowLoading(url);

    if (!isNil(filesToUpload)) {
      await Promise.all(
        filesToUpload.map(item => (
          uploadFile(item.file.data, url)
        ))
      ).then(responses => {
        responses.forEach((response, index) => {
          if (response.type === 'success') {
            this.handleChange(response.data, filesToUpload[index].name, filesToUpload[index].type);
          }
        });

        dispatchHideLoading(url);

        this.setState({
          filesToUpload: null
        });
      });

      return true;
    }

    dispatchHideLoading(url);
    dispatchShowError(__t(`${msgKey}.upload_error`));

    return false;
  };

  handleModeSwitch = (settingsGroup: string | null) => {
    if (settingsGroup === null) {
      this.setState({ editMode: false });
    } else {
      this.handleSwitchToEditMode();
    }
  };

  handleSwitchToEditMode = () => this.setState({ editMode: true });

  preparePayload = (): OrganizationSettingsForm => {
    const { organizationDetails } = this.state;

    const trimmedPayload = prepareSettingsPayload(organizationDetails);
    trimmedPayload.businessDetails.website = addProtocol(trimmedPayload.businessDetails?.website);

    return trimmedPayload;
  };

  // save on modal button save and validate
  handleSave = () => {
    const { areDetailsMissing, isModified, skipBusinessDetails, filesToUpload } = this.state;
    const {
      settingsId,
      subcampaignId,
      onContinue,
      dispatchSaveOrganizationSettings,
      dispatchShowConfirmModal,
      dispatchHideConfirmModal
    } = this.props;

    const includesFileChange = !isNil(filesToUpload);
    let fileHandlerResult: boolean | null = null;

    if (!isModified && !areDetailsMissing) {
      this.setState({
        editMode: false
      });

      return;
    }

    dispatchShowConfirmModal({
      message: __t(`${msgKey}.settings_change_modal.message`),
      title: __t(`${msgKey}.settings_change_modal.title`),
      confirmButtonLabel: __t(`${msgKey}.settings_change_modal.confirm_btn`),
      cancelButtonLabel: __t(`${msgKey}.settings_change_modal.cancel_btn`),
      onConfirm: async () => {
        dispatchHideConfirmModal();

        // upload file to S3
        if (!isNil(filesToUpload)) {
          fileHandlerResult = await this.handleUpload(filesToUpload);
        }

        if (!includesFileChange || fileHandlerResult) {
          const trimmedPayload = this.preparePayload();

          dispatchSaveOrganizationSettings(settingsId, trimmedPayload, {
            subcampaignId,
            businessDetailsMissing: skipBusinessDetails
          })
            .then(() => {
              addUserAction({
                eventCategory: 'business-bank-details',
                eventAction: 'business-bank-details-updated',
                eventLabel: 'desktop | campaign-checkin'
              });

              if (skipBusinessDetails) {
                dispatchShowConfirmModal({
                  message: __t(`${msgKey}.missing_business_details_modal.message`),
                  title: __t(`${msgKey}.missing_business_details_modal.title`),
                  confirmButtonLabel: __t(`${msgKey}.missing_business_details_modal.confirm_btn`),
                  onConfirm: () => {
                    dispatchHideConfirmModal();
                    onContinue();
                  }
                });
              } else {
                this.setState({
                  editMode: false,
                  areDetailsMissing: false,
                  filesToUpload: null
                });
              }
            })
            .catch((e) => {
              logError(e);
            });
        }
      },
      onCancel: dispatchHideConfirmModal
    });
  };

  handleCancel = () => {
    const {
      onCloseModal,
      dispatchShowConfirmModal,
      dispatchHideConfirmModal,
      dispatchNotifyThatEditingIsNotAnOption
    } = this.props;
    const { editMode } = this.state;

    if (this.areDetailsMissing() && editMode) {
      dispatchShowConfirmModal({
        message: __t(`${msgKey}.cancel_accept_offer.message`),
        title: __t(`${msgKey}.cancel_accept_offer.title`),
        confirmButtonLabel: __t(`${msgKey}.cancel_accept_offer.confirm_btn`),
        cancelButtonLabel: __t(`${msgKey}.cancel_accept_offer.cancel_btn`),
        onConfirm: () => {
          onCloseModal();
          dispatchHideConfirmModal();
        },
        onCancel: dispatchHideConfirmModal
      });

      return;
    }

    if (dispatchNotifyThatEditingIsNotAnOption) {
      dispatchNotifyThatEditingIsNotAnOption();

      return;
    }

    this.setState({
      editMode: !editMode,
      isModified: false,
      organizationDetails: this.getOrganizationDetailsState(this.props)
    });
  };

  renderBankDetailsForm() {
    const { editMode, disclaimer, organizationDetails } = this.state;
    const { bankDetails, proofDocuments } = organizationDetails;

    if (!editMode) {
      return (
        <BankDetailsSettings
          values={organizationDetails}
        />
      );
    }

    return (
      <BankDetailsSettingsForm
        direction="column"
        labelType="highlighted"
        onTextInput={this.handleTextInput}
        onChange={this.handleChange}
        onPreUpload={this.handlePreUpload}
        canEdit
        values={{
          bankDetails,
          bankDetailsProof: proofDocuments?.bankDetailsProof || null
        }}
      >
        <FormField
          label={__t(`${msgKey}.disclaimer_title`)}
          labelType="highlighted"
          direction="column"
          fontSize="small"
          showRequiredMark
          validators={[
            isRequired(disclaimer)
          ]}
        >
          <InputCheckbox
            id="disclaimer"
            type="text"
            name="disclaimer"
            checked={disclaimer}
            labelClass="business-bank-details__disclaimer-checkbox"
            label={__t(`${msgKey}.disclaimer_note`)}
            onChange={() => this.setState(state => ({ disclaimer: !state.disclaimer }))}
          />
        </FormField>
      </BankDetailsSettingsForm>
    );
  }

  renderBusinessDetailsForm() {
    const { editMode, skipBusinessDetails, organizationDetails } = this.state;
    const { businessDetails, billingAddress, proofDocuments } = organizationDetails;


    if (!editMode) {
      return (
        <InfluencerBusinessDetailsSettings
          values={this.state.organizationDetails}
        />
      );
    }

    return (
      <InfluencerBusinessDetailsSettingsForm
        optional={skipBusinessDetails}
        direction="column"
        labelType="highlighted"
        hidePhone
        onTextInput={this.handleTextInput}
        onLocationInput={this.handleLocationInput}
        onChange={this.handleChange}
        onPreUpload={this.handlePreUpload}
        canEdit
        values={{
          businessDetails,
          billingAddress,
          businessDetailsProof: proofDocuments?.businessDetailsProof || null
        }}
      />
    );
  }

  renderActions() {
    const { editMode } = this.state;
    const { onContinue } = this.props;

    return (
      <ModalFrameFooter>
        <ActionButton
          color="secondary"
          onClick={this.handleCancel}
        >
          {editMode ? __t(`${msgKey}.buttons.cancel`) : __t(`${msgKey}.buttons.edit_information`)}
        </ActionButton>
        <ActionButton
          type={editMode ? 'submit' : 'button'}
          color="primary"
          onClick={editMode ? undefined : onContinue}
        >
          {editMode ? __t(`${msgKey}.buttons.save_changes`) : __t(`${msgKey}.buttons.confirm`)}
        </ActionButton>
      </ModalFrameFooter>
    );
  }

  renderPopover = (type: 'business' | 'bank') => (
    <div className="business-bank-details__popover-label">
      <Popover
        showQuestionMark
        classNamePopover="business-bank-details__popover"
      >
        <div className="business-bank-details__popover-title">
          {__t(`${msgKey}.${type}_details_popover`)}
        </div>
      </Popover>
    </div>
  );

  render(): React.ReactNode {
    const { minHeight } = this.props;
    const { editMode, areDetailsMissing, skipBusinessDetails, areBusinessDetailsMissing } = this.state;

    return (
      <React.Fragment>
        <ModalFrameHeader
          title={areDetailsMissing ?
            __t(`${msgKey}.missing_info_title`) : __t(`${msgKey}.${editMode ? 'edit_' : ''}title`)}
        />
        <ModalFrameInner
          minHeight={minHeight}
          narrowContent
          className="business-bank-details__inner-frame"
        >
          {
            editMode && (
              <ModalFrameMessage
                message={__t(`${msgKey}.provide_bank_business_details_title`)}
                className="business-bank-details__sub-msg"
              />
            )
          }
          <Form
            key={editMode.toString()}
            onSubmit={this.handleSave}
          >
            <div className="business-bank-details__form">
              <div className="business-bank-details__sub-title">
                {__t(`${msgKey}.business_details_title`)}
                {this.renderPopover('business')}
              </div>
              {
                !editMode && (
                  <div>
                    {__t(`${msgKey}.disclaimer`)}
                  </div>
                )
              }
              <div className="business-bank-details__view">
                {this.renderBusinessDetailsForm()}
              </div>
              {
                editMode && areBusinessDetailsMissing && (
                  <div className="business-bank-details__business-details-skip">
                    <InputCheckbox
                      id="skip-business-details"
                      type="text"
                      name="disclaimer"
                      checked={skipBusinessDetails}
                      labelClass="business-bank-details__skip-business-details-note"
                      label={__t(`${msgKey}.skip_business_details_note`)}
                      onChange={() => this.setState(state => ({ skipBusinessDetails: !state.skipBusinessDetails }))}
                    />
                  </div>
                )
              }
              <div className="business-bank-details__sub-title">
                {__t(`${msgKey}.bank_details_title`)}
                {this.renderPopover('bank')}
              </div>
              <div className="business-bank-details__view">
                {this.renderBankDetailsForm()}
              </div>
            </div>
            <div>
              {this.renderActions()}
            </div>
          </Form>
        </ModalFrameInner>
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch, { isTalentAgency }: OwnProps): DispatchProps => ({
  dispatchShowConfirmModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmModal: () => dispatch(hideConfirmModal()),
  dispatchShowLoading: (url: string) => dispatch(showLoading(url)),
  dispatchHideLoading: (url: string) => dispatch(hideLoading(url)),
  dispatchShowError: (errorMessage: string) => dispatch(danger(errorMessage)),
  dispatchSaveOrganizationSettings: (id: number, data: OrganizationSettingsForm, params: OrganizationSettingsParams) =>
    dispatch(saveOrganizationSettings(id, data, params)),
  dispatchNotifyThatEditingIsNotAnOption: isTalentAgency
    ? () => dispatch(showBusinessAndBankDetailsConfirmModal(
      () => dispatch(hideBusinessAndBankDetailsConfirmModal()))
    )
    : undefined
});

export default connect(null, mapDispatchToProps)(BusinessBankDetails);
